import React, { Component } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Table,
} from "reactstrap";
import {
  Box,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { connect } from "react-redux";
import { enqueueSnackbar } from "notistack";
import {
  getListOfAccts,
  getListOfOrgsAndAccts,
  deleteUser,
  clearAllUserListMessages,
} from "./../../store/actions";
import { shouldFilterUsername } from "../../helpers/util_helper";
import getOrgCode from "../../helpers/jwt-token-access/getOrgCode";
import {
  ExtractPhoneNumber,
  ExtractCountryCallingCode,
} from "../UserConfigurationDetails/ExtractPhoneNumber";
import {
  EditAccountSortableHeader,
  EditAccountTablePagination,
  getDateSortValue,
} from "./editAccountTableControls";
import {
  AccountDetailTooltip,
  TruncatedTooltipText,
} from "./editAccountTooltipStyles";

const USERS_DESCRIPTION =
  "User accounts that have access to the data from this account.";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.25,
  px: 1,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.25,
  px: 1,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const DELETE_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #fecaca",
  backgroundColor: "#fef2f2",
  color: "#dc2626",
  padding: 0,
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  "&.Mui-disabled": {
    color: "#fca5a5",
    borderColor: "#fee2e2",
    backgroundColor: "#fff",
  },
};

const formatDisplayDate = (dateInput) => {
  if (dateInput == null || dateInput === "" || dateInput === "null") return "";
  if (typeof dateInput === "object" && dateInput.date) {
    const { year, month, day } = dateInput.date;
    const dt = new Date(year, month - 1, day);
    if (isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const dt = new Date(dateInput);
  if (isNaN(dt.getTime())) return String(dateInput);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const displayValue = (value) => {
  if (value == null || value === "" || value === "null" || value === "undefined") {
    return "";
  }
  return String(value);
};

const getUserRole = (user) =>
  displayValue(
    user?.userRolesDTOS?.[0]?.roleName ??
      user?.userRolesDtos?.[0]?.roleName ??
      user?.role ??
      user?.roles ??
      ""
  );

const getDateAssigned = (user) =>
  formatDisplayDate(
    user?.dateAssigned ??
      user?.assignedDate ??
      user?.createdDate ??
      user?.enabledDate ??
      null
  );

const getLastLogin = (user) =>
  formatDisplayDate(
    user?.lastLoginDate ?? user?.lastLogin ?? user?.lastlogin ?? null
  );

const COLUMN_HEADERS = [
  { label: "First Name", align: "left", width: "11%", field: "firstName" },
  { label: "Last Name", align: "left", width: "11%", field: "lastName" },
  { label: "User Name", align: "left", width: "16%", field: "userName" },
  { label: "Email", align: "left", width: "18%", field: "email" },
  { label: "Role", align: "left", width: "12%", field: "role" },
  { label: "Date Assigned", align: "left", width: "12%", field: "dateAssigned" },
  { label: "Last Login", align: "left", width: "12%", field: "lastLogin" },
  { label: "Action", align: "center", width: "8%", field: null },
];

class ViewAccountList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isAlertOpen: false,
      userModal: false,
      userList: [],
      search: "",
      deleteConfirmation: null,
      page: 0,
      rowsPerPage: 5,
      orderBy: "firstName",
      order: "asc",
    };
  }

  static getDerivedStateFromProps(nextProps) {
    if (nextProps.accounts !== undefined) {
      return {
        userList: nextProps.userList ? nextProps.userList : [],
      };
    }
    return null;
  }

  onNewUserCreated = (newUser) => {
    this.setState({ userList: [...this.state.userList, newUser] });
  };

  componentDidUpdate(prevProps, prevState) {
    const { deleteUserMessage, deleteUserError } = this.props;
    if (
      deleteUserMessage &&
      deleteUserMessage !== prevProps.deleteUserMessage
    ) {
      enqueueSnackbar(deleteUserMessage, { variant: "success" });
      this.setState({ deleteConfirmation: null });
      this.props.getListOfAccts();
      this.props.getListOfOrgsAndAccts();
      this.props.clearAllUserListMessages();
    }
    if (deleteUserError && deleteUserError !== prevProps.deleteUserError) {
      enqueueSnackbar(deleteUserError, { variant: "error" });
      this.props.clearAllUserListMessages();
    }

    const filtered = this.getFilteredUsers();
    const pageCount = Math.max(
      1,
      Math.ceil(filtered.length / this.state.rowsPerPage) || 1
    );
    if (this.state.page > pageCount - 1 && this.state.page !== 0) {
      this.setState({ page: 0 });
    }
  }

  handleSort = (field) => {
    this.setState((prev) => {
      if (prev.orderBy === field) {
        return { order: prev.order === "asc" ? "desc" : "asc" };
      }
      return { orderBy: field, order: "asc" };
    });
  };

  getSortedUsers = (users) => {
    const { orderBy, order } = this.state;
    const rows = [...users];
    rows.sort((a, b) => {
      let cmp = 0;
      switch (orderBy) {
        case "lastName":
          cmp = displayValue(a.lastName).localeCompare(
            displayValue(b.lastName)
          );
          break;
        case "userName":
          cmp = displayValue(a.userName).localeCompare(
            displayValue(b.userName)
          );
          break;
        case "email":
          cmp = displayValue(a.email).localeCompare(displayValue(b.email));
          break;
        case "role":
          cmp = getUserRole(a).localeCompare(getUserRole(b));
          break;
        case "dateAssigned":
          cmp =
            getDateSortValue(
              a.dateAssigned ?? a.assignedDate ?? a.createdDate ?? a.enabledDate
            ) -
            getDateSortValue(
              b.dateAssigned ?? b.assignedDate ?? b.createdDate ?? b.enabledDate
            );
          break;
        case "lastLogin":
          cmp =
            getDateSortValue(a.lastLoginDate ?? a.lastLogin ?? a.lastlogin) -
            getDateSortValue(b.lastLoginDate ?? b.lastLogin ?? b.lastlogin);
          break;
        case "firstName":
        default:
          cmp = displayValue(a.firstName).localeCompare(
            displayValue(b.firstName)
          );
          break;
      }
      return order === "asc" ? cmp : -cmp;
    });
    return rows;
  };

  closeUserModal = () => this.setState({ userModal: false });

  requestDeleteUser = (user) => {
    this.setState({
      deleteConfirmation: { ...user, action: "delete" },
    });
  };

  cancelDeleteUser = () => {
    this.setState({ deleteConfirmation: null });
  };

  confirmDeleteUser = () => {
    const pending = this.state.deleteConfirmation;
    if (!pending) return;

    const email = (pending.email || pending.userName || "").toLowerCase();
    const orgUser = (this.props.usersConfig || []).find(
      (u) =>
        (u.email || "").toLowerCase() === email ||
        (u.username || "").toLowerCase() === email
    );
    const source = orgUser || pending;

    const acccodes =
      source?.listacct?.map((account) => ({
        acccode: account.accountCode,
      })) ||
      (this.props.accountCode
        ? [{ acccode: this.props.accountCode }]
        : []);

    const resourceGroupDTOS =
      source?.userGroup?.map((userGroup) => ({
        resourcename: userGroup.resourcename,
      })) || [];

    const userRolesDTOS =
      source?.roles ||
      source?.userRolesDTOS ||
      [];

    const phoneRaw = source.phoneNumber || source.phonenumber || "";
    const orgCode =
      this.props.organizations?.orgcode ||
      this.props.organizations?.orgCode ||
      getOrgCode();

    const deleteApiPayload = {
      firstName: source.firstname || source.firstName,
      lastName: source.lastName,
      userName: source.username || source.userName || source.email,
      email: source.email,
      username: source.email || source.username || source.userName,
      firstname: source.firstname || source.firstName,
      country: source.country,
      phoneNumber: phoneRaw ? ExtractPhoneNumber(phoneRaw) : null,
      countryCode: phoneRaw
        ? ExtractCountryCallingCode(phoneRaw)
        : source.countryCode || null,
      orgCode,
      acccodes,
      userRolesDTOS,
      resourceGroupDTOS,
      action: "delete",
    };

    this.props.deleteUser(deleteApiPayload);
  };

  getFilteredUsers = () => {
    const emailDomain = process.env.REACT_APP_EMAIL_DOMAIN;
    const q = (this.state.search || "").trim().toLowerCase();

    return (this.state.userList || [])
      .filter(
        (user) =>
          !(user.email || "").includes(emailDomain || "") &&
          !shouldFilterUsername(user.userName)
      )
      .filter((user) => {
        if (!q) return true;
        const haystack = [
          user.firstName,
          user.lastName,
          user.userName,
          user.email,
          getUserRole(user),
          getDateAssigned(user),
          getLastLogin(user),
        ]
          .map((v) => displayValue(v).toLowerCase())
          .join(" ");
        return haystack.includes(q);
      });
  };

  renderEmbeddedUsersCard = () => {
    const filtered = this.getFilteredUsers();
    const sorted = this.getSortedUsers(filtered);
    const { page, rowsPerPage, orderBy, order } = this.state;
    const pagedUsers = sorted.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          p: 2.5,
          backgroundColor: "#f3f4f6",
          width: "100%",
        }}
      >
        <Box
          sx={{
            pb: 1.25,
            mb: 1.75,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography
            sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}
          >
            CloudCatcher Users
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              lineHeight: 1.5,
              flex: 1,
              minWidth: { xs: "100%", sm: 240 },
            }}
          >
            {USERS_DESCRIPTION}
          </Typography>
          <TextField
            size="small"
            placeholder="Search Users"
            value={this.state.search}
            onChange={(e) =>
              this.setState({ search: e.target.value, page: 0 })
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 280 },
              maxWidth: 360,
              flexShrink: 0,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
                backgroundColor: "#fff",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": {
                  borderColor: "#111827",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": { py: "8px" },
            }}
          />
        </Box>

        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "hidden", width: "100%" }}>
            <MuiTable size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
              <colgroup>
                {COLUMN_HEADERS.map((header) => (
                  <col key={header.label} style={{ width: header.width }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {COLUMN_HEADERS.map((header) =>
                    header.field ? (
                      <EditAccountSortableHeader
                        key={header.label}
                        field={header.field}
                        orderBy={orderBy}
                        order={order}
                        onSort={this.handleSort}
                        align={header.align}
                        headerCellSx={HEADER_CELL_SX}
                      >
                        {header.label}
                      </EditAccountSortableHeader>
                    ) : (
                      <TableCell
                        key={header.label}
                        align={header.align}
                        sx={HEADER_CELL_SX}
                      >
                        {header.label}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedUsers.map((user, index) => {
                  const firstName = displayValue(user.firstName);
                  const lastName = displayValue(user.lastName);
                  const userName = displayValue(user.userName);
                  const email = displayValue(user.email);
                  const role = getUserRole(user);
                  const dateAssigned = getDateAssigned(user);
                  const lastLogin = getLastLogin(user);
                  const cellValues = [
                    firstName,
                    lastName,
                    userName,
                    email,
                    role,
                    dateAssigned,
                    lastLogin,
                  ];

                  return (
                    <TableRow
                      key={user.email || user.userName || index}
                      sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                    >
                      {cellValues.map((value, cellIndex) => (
                        <TableCell
                          key={COLUMN_HEADERS[cellIndex].label}
                          align="left"
                          sx={BODY_CELL_SX}
                        >
                          <TruncatedTooltipText sx={CELL_TEXT}>
                            {value}
                          </TruncatedTooltipText>
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <AccountDetailTooltip title="Delete User">
                          <IconButton
                            size="small"
                            aria-label="Delete user"
                            disabled={!!this.props.deleteUserLoading}
                            onClick={() => this.requestDeleteUser(user)}
                            sx={DELETE_BTN_SX}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </AccountDetailTooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          </TableContainer>
          <EditAccountTablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={sorted.length}
            onPageChange={(nextPage) => this.setState({ page: nextPage })}
            onRowsPerPageChange={(value) =>
              this.setState({ rowsPerPage: value, page: 0 })
            }
          />
        </Box>
      </Box>
    );
  };

  renderTable = () => (
    <Row>
      <Col lg={12}>
        <div
          className="table-responsive mt-3"
          style={{ maxHeight: "500px", overflowY: "auto" }}
        >
          <Table
            className="table-centered datatable dt-responsive nowrap"
            style={{
              borderCollapse: "collapse",
              borderSpacing: 0,
              width: "100%",
            }}
          >
            <thead style={{ width: "20px", backgroundColor: "#a9a9a9" }}>
              <tr>
                <th style={{ width: "20px" }}></th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Username</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {this.state.userList
                .filter(
                  (user) =>
                    !user.email.includes(process.env.REACT_APP_EMAIL_DOMAIN) &&
                    !shouldFilterUsername(user.userName)
                )
                .map((user, index) => (
                  <tr key={index}>
                    <td></td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>{user.userName}</td>
                    <td>{user.email}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </Col>
    </Row>
  );

  render() {
    const deleteTarget =
      this.state.deleteConfirmation?.email ||
      this.state.deleteConfirmation?.userName ||
      this.state.deleteConfirmation?.username ||
      "";

    const deleteConfirmModal = (
      <Dialog
        open={!!this.state.deleteConfirmation}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || this.props.deleteUserLoading) return;
          this.cancelDeleteUser();
        }}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            width: { xs: "calc(100% - 24px)", sm: "100%" },
            m: { xs: 1.5, sm: 2 },
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#F98125",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Delete User
          </Typography>
          <button
            type="button"
            aria-label="Close"
            onClick={this.cancelDeleteUser}
            disabled={!!this.props.deleteUserLoading}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: this.props.deleteUserLoading ? "not-allowed" : "pointer",
              opacity: this.props.deleteUserLoading ? 0.6 : 1,
              boxShadow: "none",
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 400,
              color: "#334155",
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            Are you sure you want to delete{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#111827" }}>
              {deleteTarget}
            </Box>
            ?
          </Typography>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            <MuiButton
              variant="outlined"
              onClick={this.cancelDeleteUser}
              disabled={!!this.props.deleteUserLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "8px",
                px: 2.5,
                color: "#475569",
                borderColor: "#cbd5e1",
                "&:hover": {
                  borderColor: "#94a3b8",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Cancel
            </MuiButton>
            <MuiButton
              variant="contained"
              onClick={this.confirmDeleteUser}
              disabled={!!this.props.deleteUserLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "8px",
                px: 2.5,
                boxShadow: "none",
                backgroundColor: "#ef4444",
                "&:hover": {
                  backgroundColor: "#dc2626",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#fca5a5",
                  color: "#fff",
                },
              }}
            >
              {this.props.deleteUserLoading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Delete"
              )}
            </MuiButton>
          </Box>
        </DialogContent>
      </Dialog>
    );

    if (this.props.embedded) {
      return (
        <React.Fragment>
          {this.renderEmbeddedUsersCard()}
          {deleteConfirmModal}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Modal
          isOpen={this.props.modal_static}
          backdrop="static"
          centered
          size="lg"
        >
          <ModalHeader toggle={this.props.closeModal}>
            List of Registered User for Account Number:{" "}
            {this.props?.accountNumber}
          </ModalHeader>
          <ModalBody>
            {this.renderTable()}
            <ModalFooter>
              <Button
                type="button"
                color="light"
                onClick={this.props.closeModal}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalBody>
        </Modal>
        {deleteConfirmModal}
      </React.Fragment>
    );
  }
}

const mapStatetoProps = (state) => {
  const { accounts, usersConfig, organizations } = state.acctList;
  const { deleteUserError, deleteUserLoading, deleteUserMessage } =
    state.userList || {};
  return {
    accounts,
    usersConfig,
    organizations:
      organizations && organizations.length > 0 ? organizations[0] : organizations,
    deleteUserError,
    deleteUserLoading,
    deleteUserMessage,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getListOfAccts: () => {
      dispatch(getListOfAccts());
    },
    getListOfOrgsAndAccts: () => {
      dispatch(getListOfOrgsAndAccts());
    },
    deleteUser: (payload) => {
      dispatch(deleteUser(payload));
    },
    clearAllUserListMessages: () => {
      dispatch(clearAllUserListMessages());
    },
  };
};

export default connect(mapStatetoProps, mapDispatchToProps)(ViewAccountList);
